import { useEffect, useState, useRef } from "react";
import { toast } from "react-toastify";
import OneSignal from "react-onesignal";
import { Oval } from "react-loader-spinner";

import UserService from "src/Services/API/user.service";
import { handleError } from "src/Common/function";
import PhoneNumberInput from "src/Components/Authentication/PhoneNumberInput";
import { getImageUrl, getAvatarUrl } from "src/Common/common";
import { FieldLabel, PrimaryButton } from "src/Pages/WO/Components/upload/uploadModalUI";
import { SecondaryButton, SettingsCard, TextField } from "./accountUI";
import ChangePasswordSection from "./ChangePasswordSection";

// The server takes PNG or JPEG avatars up to 2MB (user.controller's upload
// limits); refuse anything else here rather than after the upload.
const AVATAR_MAX_BYTES = 2 * 1024 * 1024;
const AVATAR_TYPES = "image/png,image/jpeg";

function Account() {
  const inputFile = useRef(null);
  const storedUser = UserService.getCurrentUser();
  // What the server has, to tell edits apart and to discard them.
  const [saved, setSaved] = useState({ name: storedUser?.name || "", phone: storedUser?.phone || "" });
  const [name, setName] = useState(saved.name);
  const [phone, setPhone] = useState(saved.phone);
  const [email, setEmail] = useState(storedUser?.email || "");
  const [phoneInvalid, setPhoneInvalid] = useState(false);
  // PhoneNumberInput keeps what was typed once edited; a new key resets it.
  const [phoneFieldKey, setPhoneFieldKey] = useState(0);
  const [nameError, setNameError] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [preview, setPreview] = useState(getImageUrl("/images/common/avatar_default.png"));
  const [uploading, setUploading] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushToggling, setPushToggling] = useState(false);

  const isDirty = name !== saved.name || phone !== saved.phone || phoneInvalid;

  useEffect(() => {
    // Own profile via the auth-only /me endpoint — getUser (get-a-user-by-id)
    // is user:read-gated and would 403 users without that permission.
    const getUser = async () => {
      try {
        const response = await UserService.getMe();
        const user = response.data;
        setSaved({ name: user.name || "", phone: user.phone || "" });
        setName(user.name || "");
        setPhone(user.phone || "");
        setEmail(user.email || "");
        setPhoneFieldKey((key) => key + 1);
        if (user.avatar) {
          setPreview(getAvatarUrl(user.avatar));
        }
      } catch (error) {
        handleError(error);
      }
    };
    getUser();
  }, []);

  useEffect(() => {
    const syncPushState = () => {
      setPushEnabled(
        Boolean(OneSignal.Notifications?.permission) && Boolean(OneSignal.User?.PushSubscription?.optedIn),
      );
    };
    syncPushState();
    OneSignal.User.PushSubscription.addEventListener("change", syncPushState);
    OneSignal.Notifications.addEventListener("permissionChange", syncPushState);
    return () => {
      OneSignal.User.PushSubscription.removeEventListener("change", syncPushState);
      OneSignal.Notifications.removeEventListener("permissionChange", syncPushState);
    };
  }, []);

  const togglePushNotification = async () => {
    if (pushToggling) return;
    setPushToggling(true);
    try {
      if (pushEnabled) {
        await OneSignal.User.PushSubscription.optOut();
        setPushEnabled(false);
        toast("Push notifications disabled");
      } else {
        if (!OneSignal.Notifications.permission) {
          await OneSignal.Notifications.requestPermission();
        }
        if (!OneSignal.Notifications.permission) {
          toast("Please allow notifications in your browser settings");
          return;
        }
        await OneSignal.User.PushSubscription.optIn();
        // Linking the subscription to the user's email (external_id) is required
        // for the backend to target them; opt-in alone is not enough.
        const user = UserService.getCurrentUser();
        if (user?.email) {
          await OneSignal.login(user.email);
        }
        setPushEnabled(true);
        toast("Push notifications enabled");
      }
    } catch (error) {
      console.log("togglePushNotification error: ", error);
      toast("Something went wrong");
    } finally {
      setPushToggling(false);
    }
  };

  const discardChanges = () => {
    setName(saved.name);
    setPhone(saved.phone);
    setPhoneInvalid(false);
    setNameError(false);
    setPhoneFieldKey((key) => key + 1);
  };

  const saveChanges = async () => {
    if (savingProfile || !isDirty) return;
    if (!name.trim()) {
      setNameError(true);
      toast("Please enter your name");
      return;
    }
    if (phoneInvalid) {
      toast("Please enter a valid phone number");
      return;
    }
    setNameError(false);
    setSavingProfile(true);
    try {
      const currentUser = UserService.getCurrentUser();
      currentUser.name = name.trim();
      currentUser.phone = phone;
      const response = await UserService.updateUser(currentUser);
      if (response.status === 201) {
        UserService.saveCurrentUser(response.data);
        const next = { name: response.data?.name ?? currentUser.name, phone: response.data?.phone ?? phone };
        setSaved(next);
        setName(next.name);
        setPhone(next.phone);
        toast("Saved successfully");
      } else {
        toast("Something went wrong");
      }
    } catch (error) {
      handleError(error);
    } finally {
      setSavingProfile(false);
    }
  };

  const uploadClicked = () => {
    if (uploading) return;
    inputFile.current.click();
  };

  const fileSelected = async (e) => {
    const avatar = e.target.files[0];
    // Clear the input before doing anything else: picking the same file twice in
    // a row fires no change event otherwise, so retrying after a failed upload —
    // or after a rejected size — would silently do nothing.
    e.target.value = "";
    if (!avatar) return;
    if (!AVATAR_TYPES.split(",").includes(avatar.type)) {
      toast("Please choose a PNG or JPG image");
      return;
    }
    if (avatar.size > AVATAR_MAX_BYTES) {
      toast("The photo must be 2MB or smaller");
      return;
    }

    // Show the picked image straight away, but keep the old one to fall back on:
    // a preview left showing an image the server never took would be a lie.
    const previous = preview;
    setPreview(URL.createObjectURL(avatar));
    setUploading(true);
    try {
      const response = await UserService.uploadAvatar(avatar, UserService.getCurrentUser().id);
      if (response.status === 201) {
        toast("Photo updated");
        UserService.saveCurrentUser(response.data);
      } else {
        setPreview(previous);
        toast("Something went wrong");
      }
    } catch (error) {
      setPreview(previous);
      handleError(error);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="p-5">
      <div className="max-w-[760px] flex flex-col gap-5">
        <div>
          <h1 className="font-OpenSansSemiBold text-[20px] leading-[28px] text-[#1A2332]">Account</h1>
          <p className="mt-0.5 font-OpenSansRegular text-[14px] leading-[20px] text-[#7C829A]">
            Your profile, notifications and password.
          </p>
        </div>

        <SettingsCard
          title="Profile"
          description="How you appear to your team and on work orders."
          footer={
            <>
              <SecondaryButton onClick={discardChanges} disabled={!isDirty || savingProfile}>
                Discard
              </SecondaryButton>
              <PrimaryButton
                onClick={saveChanges}
                disabled={!isDirty}
                loading={savingProfile}
                loadingLabel="Saving..."
              >
                Save changes
              </PrimaryButton>
            </>
          }
        >
          <div className="flex items-center gap-5">
            <button
              type="button"
              onClick={uploadClicked}
              title="Change photo"
              className="relative w-[88px] h-[88px] shrink-0 rounded-full overflow-hidden border border-[#E5E8F3] bg-[#F8F9FE] cursor-pointer"
            >
              <img src={preview} alt="" className="w-full h-full object-cover" />
              {uploading && (
                <span className="absolute inset-0 flex items-center justify-center bg-white/70">
                  <Oval height="24" width="24" color="#0065FD" strokeWidth={6} secondaryColor="#C7DCFF" visible />
                </span>
              )}
            </button>
            <input
              onChange={fileSelected}
              type="file"
              ref={inputFile}
              className="hidden"
              multiple={false}
              accept={AVATAR_TYPES}
            />
            <div className="flex flex-col gap-2 min-w-0">
              <div className="min-w-0">
                <p className="font-OpenSansSemiBold text-[16px] leading-[24px] text-[#1A2332] truncate">
                  {saved.name || "—"}
                </p>
                <p className="font-OpenSansRegular text-[13px] leading-[20px] text-[#7C829A] truncate">{email}</p>
              </div>
              <div className="flex items-center gap-3">
                <SecondaryButton onClick={uploadClicked} disabled={uploading}>
                  {uploading ? "Uploading..." : "Change photo"}
                </SecondaryButton>
                <span className="font-OpenSansRegular text-[12px] leading-[18px] text-[#7C829A]">
                  PNG or JPG, up to 2MB
                </span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mt-6">
            <TextField
              label="Full name"
              value={name}
              onChange={(value) => {
                setName(value);
                if (nameError) setNameError(false);
              }}
              placeholder="ex: John Smith"
              error={nameError}
              autoComplete="name"
              onEnter={saveChanges}
            />
            <div className="flex flex-col gap-1">
              <FieldLabel>Phone</FieldLabel>
              <PhoneNumberInput
                key={phoneFieldKey}
                label={null}
                variant="card"
                value={phone}
                onChange={setPhone}
                onInvalidChange={setPhoneInvalid}
              />
            </div>
            <TextField label="Email" value={email} readOnly />
          </div>
        </SettingsCard>

        <SettingsCard title="Notifications" description="Where you hear about new work and messages.">
          <div className="flex items-center justify-between gap-6">
            <div>
              <p className="font-OpenSansSemiBold text-[14px] leading-[20px] text-[#1A2332]">
                Browser push notifications
              </p>
              <p className="font-OpenSansRegular text-[13px] leading-[20px] text-[#7C829A]">
                Get notified in this browser, even when Makani isn't the open tab.
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={pushEnabled}
              aria-label="Browser push notifications"
              disabled={pushToggling}
              onClick={togglePushNotification}
              className={`relative inline-flex h-[24px] w-[44px] shrink-0 items-center rounded-full transition-colors duration-200 disabled:opacity-50 ${
                pushEnabled ? "bg-[#0065FD]" : "bg-[#C9CEE2]"
              }`}
            >
              <span
                className={`inline-block h-[18px] w-[18px] transform rounded-full bg-white transition-transform duration-200 ${
                  pushEnabled ? "translate-x-[23px]" : "translate-x-[3px]"
                }`}
              />
            </button>
          </div>
        </SettingsCard>

        <ChangePasswordSection />
      </div>
    </div>
  );
}

export default Account;
